import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { dashboardAPI } from '@/api/api';
import { 
  Users, BookOpen, DollarSign, TrendingUp, UserCheck, Calendar, 
  UserPlus, FileText, Bell, Eye, CheckCircle, GraduationCap, 
  Cake, Megaphone, Clock, ArrowRight, Wallet, PiggyBank,
  ChevronRight, Sparkles, Target, Award, Building2, Phone, MessageCircle, Check
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import StudentDashboard from './dashboards/StudentDashboard';
import ParentDashboard from './dashboards/ParentDashboard';
import TeacherDashboard from './dashboards/TeacherDashboard';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import SupportChatBot from '@/components/support/SupportChatBot';
import { useModuleAccess } from '@/hooks/useModuleAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user } = useAuth();
  const { isAdmin, isModuleEnabled } = useModuleAccess();
  const canViewFinance = isAdmin || isModuleEnabled('accounts') || isModuleEnabled('fees');
  const [subStatus, setSubStatus] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/subscriptions/my-status`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setSubStatus(d))
      .catch(() => {});
  }, [isAdmin]);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marksNotifications, setMarksNotifications] = useState([]);
  const [marksUnread, setMarksUnread] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [birthdays, setBirthdays] = useState([]);
  const [birthdayMeta, setBirthdayMeta] = useState({ whatsapp_configured: false, tenant_name: '' });
  const [notices, setNotices] = useState([]);
  const [feeSummary, setFeeSummary] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [sendingWish, setSendingWish] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchWidgetsData();
    if (user?.role === 'Admin' || user?.role === 'SuperAdmin') {
      fetchMarksNotifications();
    }
    
    // Update time every minute
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, chartRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getChartData()
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data.chart_data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWidgetsData = async () => {
    try {
      const [birthdaysRes, noticesRes, feeRes, quickRes] = await Promise.all([
        dashboardAPI.getBirthdaysToday().catch(() => ({ data: { birthdays: [], whatsapp_configured: false, tenant_name: '' } })),
        dashboardAPI.getRecentNotices().catch(() => ({ data: { notices: [] } })),
        dashboardAPI.getFeeSummary().catch(() => ({ data: null })),
        dashboardAPI.getQuickStats().catch(() => ({ data: null }))
      ]);
      
      setBirthdays(birthdaysRes.data.birthdays || []);
      setBirthdayMeta({
        whatsapp_configured: birthdaysRes.data.whatsapp_configured || false,
        tenant_name: birthdaysRes.data.tenant_name || ''
      });
      setNotices(noticesRes.data.notices || []);
      setFeeSummary(feeRes.data);
      setQuickStats(quickRes.data);
    } catch (error) {
      console.error('Error fetching widgets data:', error);
    }
  };

  const fetchMarksNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/marks-entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMarksNotifications(data.notifications || []);
        setMarksUnread(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching marks notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/marks-entries/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setMarksNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setMarksUnread(prev => Math.max(0, prev - 1));
        toast.success('Marked as read');
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle sending birthday wish
  const handleSendBirthdayWish = async (person) => {
    if (!person.phone) {
      toast.error('No phone number available');
      return;
    }

    setSendingWish(person.id);

    try {
      // If WhatsApp API is configured, use API to send
      if (birthdayMeta.whatsapp_configured) {
        const response = await dashboardAPI.sendBirthdayWish(person.id, person.type, 'auto');
        if (response.data.success) {
          toast.success(`Birthday wish sent to ${person.name}!`);
          // Update local state
          setBirthdays(prev => prev.map(p => 
            p.id === person.id ? { ...p, wish_sent: true } : p
          ));
        } else if (response.data.already_sent) {
          toast.info('Wish already sent today');
          setBirthdays(prev => prev.map(p => 
            p.id === person.id ? { ...p, wish_sent: true } : p
          ));
        }
      } else {
        // Manual: Open WhatsApp Web with pre-filled message
        const message = encodeURIComponent(
          `🎂 Happy Birthday ${person.name}! Wishing you a wonderful year ahead. - From ${birthdayMeta.tenant_name || 'Our Institution'}`
        );
        const phone = person.phone.replace(/\D/g, ''); // Remove non-digits
        const phoneWithCountry = phone.startsWith('91') ? phone : `91${phone}`;
        
        // Open WhatsApp Web
        window.open(`https://wa.me/${phoneWithCountry}?text=${message}`, '_blank');
        
        // Mark as sent in backend
        await dashboardAPI.sendBirthdayWish(person.id, person.type, 'manual');
        
        toast.success(`WhatsApp opened for ${person.name}`);
        setBirthdays(prev => prev.map(p => 
          p.id === person.id ? { ...p, wish_sent: true } : p
        ));
      }
    } catch (error) {
      console.error('Error sending wish:', error);
      toast.error('Failed to send wish');
    } finally {
      setSendingWish(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-indicator">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Route to role-specific dashboards
  if (user?.role === 'Student') return <StudentDashboard />;
  if (user?.role === 'Parent') return <ParentDashboard />;
  if (user?.role === 'Teacher') return <TeacherDashboard />;

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('en-IN', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const quickActions = [
    { icon: UserPlus, label: 'Add Student', href: '/students/admission', color: 'from-blue-500 to-cyan-500', module: 'admission' },
    { icon: UserCheck, label: 'Attendance', href: '/attendance/mark', color: 'from-green-500 to-emerald-500', module: 'attendance' },
    { icon: DollarSign, label: 'Fee Collection', href: '/fees/pay-fee', color: 'from-purple-500 to-pink-500', module: 'fees' },
    { icon: FileText, label: 'Reports', href: '/reports/fees', color: 'from-orange-500 to-amber-500', module: 'reports' },
  ].filter(action => isModuleEnabled(action.module));

  // Fee data from API - FIXED: No hardcoded fallback
  const feeData = feeSummary ? [
    { name: 'Collected', value: feeSummary.collected_percent || 0, color: '#10b981' },
    { name: 'Pending', value: feeSummary.pending_percent || 0, color: '#f59e0b' },
  ] : [
    { name: 'Collected', value: 0, color: '#10b981' },
    { name: 'Pending', value: 0, color: '#f59e0b' },
  ];

  // Attendance from quick stats or fallback
  const attendancePercent = quickStats?.student_attendance_percent || stats?.daily_kpis?.student_attendance || 0;
  const staffAttendancePercent = quickStats?.staff_attendance_percent || stats?.daily_kpis?.staff_attendance || 0;

  return (
    <div className="space-y-6 pb-8" data-testid="dashboard-page">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="h-6 w-6 text-yellow-300" />
              <span className="text-blue-100 text-sm font-medium">Welcome back!</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              {user?.full_name || user?.name || 'Administrator'}
            </h1>
            <p className="text-blue-100 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {formatDate(currentTime)}
            </p>
          </div>
          
          <div className="hidden md:flex items-center gap-4">
            <div className="text-right">
              <p className="text-blue-100 text-sm">Current Time</p>
              <p className="text-2xl font-bold">
                {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Building2 className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Validity Banner (Admin only) */}
      {isAdmin && subStatus && subStatus.days_remaining !== null && subStatus.days_remaining !== undefined && (
        <div data-testid="subscription-validity-card" className={`rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3 shadow-sm ${
          subStatus.days_remaining > 30 ? 'bg-emerald-50 border-emerald-200' :
          subStatus.days_remaining > 7 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              subStatus.days_remaining > 30 ? 'bg-emerald-100' : subStatus.days_remaining > 7 ? 'bg-amber-100' : 'bg-red-100'
            }`}>
              <Calendar className={`h-5 w-5 ${
                subStatus.days_remaining > 30 ? 'text-emerald-600' : subStatus.days_remaining > 7 ? 'text-amber-600' : 'text-red-600'
              }`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {subStatus.plan_name ? `${subStatus.plan_name} Plan` : 'Subscription'} — valid till {subStatus.subscription_end_date || subStatus.end_date || 'N/A'}
              </p>
              <p className="text-xs text-gray-500">Panel access expires after this date</p>
            </div>
          </div>
          <div className="text-right">
            <p data-testid="subscription-days-remaining" className={`text-2xl font-bold ${
              subStatus.days_remaining > 30 ? 'text-emerald-600' : subStatus.days_remaining > 7 ? 'text-amber-600' : 'text-red-600'
            }`}>{subStatus.days_remaining} days</p>
            <p className="text-xs text-gray-500">remaining</p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action, index) => (
          <a
            key={index}
            href={action.href}
            className={`group relative overflow-hidden bg-gradient-to-br ${action.color} p-4 rounded-xl text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500"></div>
            <action.icon className="h-8 w-8 mb-2" />
            <p className="font-semibold">{action.label}</p>
            <ChevronRight className="absolute bottom-4 right-4 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        ))}
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <GradientStatCard
          icon={GraduationCap}
          title="Total Students"
          value={stats?.summary?.total_students || 0}
          gradient="from-blue-500 to-cyan-400"
          trend="+12%"
          trendUp={true}
        />
        <GradientStatCard
          icon={Users}
          title="Total Staff"
          value={stats?.summary?.total_staff || 0}
          gradient="from-purple-500 to-pink-400"
          trend="+5%"
          trendUp={true}
        />
        {canViewFinance && (
          <GradientStatCard
            icon={Wallet}
            title="Total Income"
            value={`₹${(stats?.summary?.total_income || 0).toLocaleString('en-IN')}`}
            gradient="from-green-500 to-emerald-400"
            trend="+18%"
            trendUp={true}
          />
        )}
        {canViewFinance && (
          <GradientStatCard
            icon={PiggyBank}
            title="Expenditure"
            value={`₹${(stats?.summary?.total_expenditure || 0).toLocaleString('en-IN')}`}
            gradient="from-orange-500 to-amber-400"
            trend="-3%"
            trendUp={false}
          />
        )}
      </div>

      {/* Middle Section - 3 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Attendance Ring Charts */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-blue-600" />
            Today's Attendance
          </h3>
          <div className="flex justify-around">
            <CircularProgress 
              percentage={attendancePercent} 
              color="#3b82f6" 
              label="Students" 
            />
            <CircularProgress 
              percentage={staffAttendancePercent} 
              color="#8b5cf6" 
              label="Staff" 
            />
          </div>
        </div>

        {/* Fee Collection Status */}
        {canViewFinance && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Fee Collection
          </h3>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie
                  data={feeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {feeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <span className="text-sm text-gray-600">Collected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-sm text-gray-600">Pending</span>
            </div>
          </div>
        </div>
        )}

        {/* Birthdays Today */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Cake className="h-5 w-5 text-pink-500" />
            Birthdays Today
            <span className="ml-auto bg-pink-100 text-pink-600 text-xs font-bold px-2 py-1 rounded-full">
              {birthdays.length}
            </span>
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {birthdays.length > 0 ? birthdays.slice(0, 5).map((person, index) => (
              <div key={person.id || index} className="flex items-center gap-3 p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0">
                  {person.photo ? (
                    <img src={person.photo} alt={person.name} className="w-full h-full object-cover" />
                  ) : (
                    person.name?.charAt(0) || '?'
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">{person.name}</p>
                  <p className="text-xs text-gray-500">{person.type} • {person.details}</p>
                  {person.phone && (
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {person.phone}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {person.wish_sent ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <Check className="h-3 w-3" /> Sent
                    </span>
                  ) : person.phone ? (
                    <button
                      onClick={() => handleSendBirthdayWish(person)}
                      disabled={sendingWish === person.id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded-full text-xs font-medium transition-colors disabled:opacity-50"
                      title="Send WhatsApp Wish"
                    >
                      <MessageCircle className="h-3 w-3" />
                      {sendingWish === person.id ? '...' : 'WhatsApp'}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">No phone</span>
                  )}
                  <span className="text-lg">🎂</span>
                </div>
              </div>
            )) : (
              <div className="text-center py-6">
                <Cake className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No birthdays today</p>
              </div>
            )}
          </div>
          {birthdayMeta.whatsapp_configured && birthdays.length > 0 && (
            <p className="text-xs text-green-600 mt-3 flex items-center gap-1">
              <Check className="h-3 w-3" /> WhatsApp API configured - Auto wishes enabled
            </p>
          )}
        </div>
      </div>

      {/* Daily KPIs - Compact Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MiniKPICard icon={UserCheck} title="Present" value={stats?.daily_kpis?.student_attendance || 0} color="text-green-600" bgColor="bg-green-50" />
        <MiniKPICard icon={UserPlus} title="Staff Present" value={stats?.daily_kpis?.staff_attendance || 0} color="text-blue-600" bgColor="bg-blue-50" />
        <MiniKPICard icon={DollarSign} title="Fee Today" value={`₹${(stats?.daily_kpis?.fee_collection || 0).toLocaleString('en-IN')}`} color="text-purple-600" bgColor="bg-purple-50" />
        <MiniKPICard icon={BookOpen} title="Bookings" value={stats?.daily_kpis?.seat_booking || 0} color="text-orange-600" bgColor="bg-orange-50" />
        <MiniKPICard icon={Users} title="Consultants" value={stats?.daily_kpis?.consultants || 0} color="text-cyan-600" bgColor="bg-cyan-50" />
        <MiniKPICard icon={Calendar} title="Meetings" value={stats?.daily_kpis?.meetings || 0} color="text-pink-600" bgColor="bg-pink-50" />
      </div>

      {/* Bottom Section - 2 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Notices */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-blue-600" />
              Recent Notices
            </h3>
            <a href="/notices" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="space-y-3">
            {notices.length > 0 ? notices.map((notice, index) => (
              <div key={index} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  notice.priority === 'high' ? 'bg-red-500' : 
                  notice.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                }`}></div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{notice.title}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3" /> {notice.time_ago}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            )) : (
              <div className="text-center py-6">
                <Megaphone className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No recent notices</p>
              </div>
            )}
          </div>
        </div>

        {/* Marks Entry Notifications */}
        {(user?.role === 'Admin' || user?.role === 'SuperAdmin') && marksNotifications.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Bell className="h-5 w-5 text-amber-500" />
                Teacher Marks Entry
                {marksUnread > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {marksUnread} new
                  </span>
                )}
              </h3>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {marksNotifications.slice(0, 4).map((notification) => (
                <div 
                  key={notification.id}
                  className={`p-3 rounded-xl border ${notification.is_read ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`font-medium text-sm ${notification.is_read ? 'text-gray-600' : 'text-gray-900'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{notification.message}</p>
                    </div>
                    {!notification.is_read && (
                      <Button size="sm" variant="ghost" onClick={() => markAsRead(notification.id)} className="text-amber-600">
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
              <Award className="h-5 w-5 text-amber-500" />
              Quick Stats
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl text-center">
                <p className="text-3xl font-bold text-blue-600">{quickStats?.active_students || stats?.summary?.total_students || 0}</p>
                <p className="text-sm text-gray-600">Active Students</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl text-center">
                <p className="text-3xl font-bold text-purple-600">{attendancePercent}%</p>
                <p className="text-sm text-gray-600">Attendance</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl text-center">
                <p className="text-3xl font-bold text-green-600">{quickStats?.total_courses || 0}</p>
                <p className="text-sm text-gray-600">Courses</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-xl text-center">
                <p className="text-3xl font-bold text-orange-600">{quickStats?.total_faculty || 0}</p>
                <p className="text-sm text-gray-600">Faculty</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Financial Chart */}
      {canViewFinance && (
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          Financial Overview
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <defs>
              <linearGradient id="colorFees" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} 
            />
            <Legend />
            <Line type="monotone" dataKey="student_fees" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2 }} name="Student Fees" />
            <Line type="monotone" dataKey="salary_paid" stroke="#ef4444" strokeWidth={3} dot={{ fill: '#ef4444', strokeWidth: 2 }} name="Salary Paid" />
            <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2 }} name="Total Income" />
            <Line type="monotone" dataKey="expenses" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', strokeWidth: 2 }} name="Total Expenses" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      )}

      {/* Support Chat Bot */}
      <SupportChatBot />
    </div>
  );
};

// Gradient Stat Card Component
const GradientStatCard = ({ icon: Icon, title, value, gradient, trend, trendUp }) => (
  <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5 group hover:shadow-lg transition-all duration-300">
    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${gradient} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500`}></div>
    <div className="flex items-start justify-between relative z-10">
      <div>
        <p className="text-gray-500 text-sm font-medium">{title}</p>
        <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        <div className={`flex items-center gap-1 mt-2 text-sm ${trendUp ? 'text-green-600' : 'text-red-500'}`}>
          <TrendingUp className={`h-4 w-4 ${!trendUp && 'rotate-180'}`} />
          <span>{trend}</span>
        </div>
      </div>
      <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </div>
);

// Circular Progress Component
const CircularProgress = ({ percentage, color, label }) => {
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 transform -rotate-90">
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke="#e5e7eb"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke={color}
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold text-gray-800">{percentage}%</span>
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-2 font-medium">{label}</p>
    </div>
  );
};

// Mini KPI Card Component
const MiniKPICard = ({ icon: Icon, title, value, color, bgColor }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${bgColor}`}>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div>
        <p className="text-xs text-gray-500">{title}</p>
        <p className="text-lg font-bold text-gray-800">{value}</p>
      </div>
    </div>
  </div>
);

export default Dashboard;
